import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileText, ShoppingCart, BellRing, User, MapPin, Euro, Clock, Mail, Phone, AlertTriangle, Eye, Share2, Facebook, Linkedin, MessageCircle, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { projectTypeLabels } from "./labels";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("fr-BE") : "—");

const BUDGET_LABELS = {
  "moins-5000": "moins de 5 000 €", "5000-10000": "5 000 à 10 000 €", "10000-20000": "10 000 à 20 000 €",
  "20000-50000": "20 000 à 50 000 €", "50000+": "plus de 50 000 €",
};

const KIND_LABELS = {
  new_lead: "Nouveau lead", unsold_48h: "Rappel 48h", unsold_7d: "Rappel J+7",
  daily_recap: "Résumé quotidien", purchase: "Achat confirmé", test: "Test",
  lead_rejected: "Lead refusé", to_validate: "À valider",
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 text-sm">
    <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
    <span className="text-slate-500 w-28 shrink-0">{label}</span>
    <span className="text-slate-900 font-medium break-words">{value || "—"}</span>
  </div>
);

const Section = ({ icon: Icon, title, count, children, testId }) => (
  <div data-testid={testId}>
    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-2">
      <Icon className="w-4 h-4 text-orange-500" />{title}
      {count !== undefined && <span className="text-xs font-normal text-slate-400">({count})</span>}
    </h3>
    {children}
  </div>
);

export const LeadDetailDialog = ({ lead, onClose, API }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!lead) return;
    setLoading(true);
    setData(null);
    axios.get(`${API}/leads/${lead.id}/detail`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [lead, API]);

  const l = data?.lead;
  const projLabel = l ? (projectTypeLabels[l.project_type] || l.project_type) : "";
  const budgetLabel = l ? (BUDGET_LABELS[l.budget] || l.budget || "") : "";
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/espace-entrepreneur` : "";
  const shareText = l ? `🔔 Nouvelle demande de devis sur DevisMaison.be : ${projLabel} à ${l.city}${l.postal_code ? ` (${l.postal_code})` : ""}${budgetLabel ? ` — budget ${budgetLabel}` : ""}. Entreprises, connectez-vous pour recevoir ce lead 👉` : "";
  const copyShare = () => {
    navigator.clipboard.writeText(`${shareText} ${shareUrl}`.trim());
    toast.success("Texte copié dans le presse-papiers");
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
            Fiche lead <span className="font-mono text-orange-600 text-base">{lead?.lead_number}</span>
          </DialogTitle>
          <DialogDescription>Détails complets, fichiers, historique des notifications et acheteurs.</DialogDescription>
        </DialogHeader>

        {loading && <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-orange-500" /></div>}
        {data?.error && <p className="text-sm text-red-600">Impossible de charger la fiche du lead.</p>}

        {l && (
          <div className="space-y-6">
            {data.duplicates.length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3" data-testid="detail-duplicates">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-red-700 mb-2">
                  <AlertTriangle className="w-4 h-4" />Doublon potentiel ({data.duplicates.length})
                </h3>
                <div className="space-y-1">
                  {data.duplicates.map((d) => (
                    <div key={d.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-700" data-testid={`detail-duplicate-${d.id}`}>
                      <span className="font-mono text-orange-600 font-bold">{d.lead_number || "—"}</span>
                      <span className="font-medium">{d.name || "—"}</span>
                      <span className="text-slate-500">{projectTypeLabels[d.project_type] || d.project_type}</span>
                      <span className="text-slate-500">{d.city}</span>
                      <span className="text-slate-400">{fmtDay(d.created_at)}</span>
                      {d.match.map((m) => (
                        <span key={m} className="px-1.5 py-0.5 rounded-full bg-red-200 text-red-800 font-semibold">
                          {m === "email" ? "même e-mail" : "même téléphone"}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <Row icon={User} label="Client" value={`${l.first_name || ""} ${l.last_name || ""}`.trim()} />
              <Row icon={FileText} label="Projet" value={projectTypeLabels[l.project_type] || l.project_type} />
              <Row icon={Mail} label="E-mail" value={l.email} />
              <Row icon={Phone} label="Téléphone" value={l.phone} />
              <Row icon={MapPin} label="Localisation" value={`${l.city || ""} ${l.postal_code || ""} (${l.country || "BE"})`} />
              <Row icon={Euro} label="Budget" value={l.budget} />
              <Row icon={Clock} label="Délai" value={l.timeline} />
              <Row icon={Eye} label="Vues sans achat" value={String(l.views_count ?? 0)} />
            </div>

            <Section icon={Share2} title="Partager la demande sur les réseaux sociaux" testId="detail-share">
              <p className="text-xs text-slate-500 mb-2">Un message sans donnée personnelle (type de travaux, ville, budget) invitant les entreprises à se connecter pour acheter le lead.</p>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 mb-3" data-testid="detail-share-preview">{shareText} {shareUrl}</div>
              <div className="flex flex-wrap items-center gap-2">
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-[#1877F2] text-white text-sm font-medium hover:opacity-90 transition-opacity" data-testid="lead-share-facebook">
                  <Facebook className="w-4 h-4" />Facebook
                </a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-[#0A66C2] text-white text-sm font-medium hover:opacity-90 transition-opacity" data-testid="lead-share-linkedin">
                  <Linkedin className="w-4 h-4" />LinkedIn
                </a>
                <a href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-[#25D366] text-white text-sm font-medium hover:opacity-90 transition-opacity" data-testid="lead-share-whatsapp">
                  <MessageCircle className="w-4 h-4" />WhatsApp
                </a>
                <button type="button" onClick={copyShare} className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-300 transition-colors" data-testid="lead-share-copy">
                  <Copy className="w-4 h-4" />Copier le texte
                </button>
              </div>
            </Section>

            <Section icon={FileText} title="Description du projet" testId="detail-description">
              <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{l.project_description || "Aucune description fournie."}</p>
            </Section>

            <Section icon={FileText} title="Fichiers joints" count={l.files?.length || 0} testId="detail-files">
              {l.files && l.files.length > 0 ? (
                <div className="space-y-1">
                  {l.files.map((f, i) => (
                    <a key={i} href={`${API}/leads/${l.id}/files/${f}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid={`detail-file-${i}`}>
                      <FileText className="w-3.5 h-3.5" />{f.split("_").slice(2).join("_") || f}
                    </a>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400">Aucun fichier.</p>}
            </Section>

            <Section icon={ShoppingCart} title="Acheteurs" count={data.buyers.length} testId="detail-buyers">
              {data.buyers.length === 0 ? <p className="text-sm text-slate-400">Ce lead n'a pas encore été vendu.</p> : (
                <div className="space-y-2">
                  {data.buyers.map((b) => (
                    <div key={b.id} className="flex items-center justify-between gap-3 text-sm py-2 border-b border-slate-100 last:border-0" data-testid={`detail-buyer-${b.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{b.company_name}</p>
                        <p className="text-xs text-slate-500 truncate">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
                      </div>
                      <div className="text-right shrink-0">
                        {b.amount != null && <p className="font-semibold text-green-600">{Number(b.amount).toFixed(2)}€</p>}
                        <p className="text-xs text-slate-400">{fmtDate(b.bought_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={BellRing} title="Historique des notifications" count={data.notifications.length} testId="detail-notifications">
              {data.notifications.length === 0 ? <p className="text-sm text-slate-400">Aucune notification envoyée pour ce lead.</p> : (
                <div className="space-y-1.5">
                  {data.notifications.map((n, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 text-sm py-1.5 border-b border-slate-100 last:border-0" data-testid={`detail-notif-${i}`}>
                      <div className="min-w-0">
                        <p className="text-slate-900 truncate">
                          <span className="inline-block px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold mr-2">{KIND_LABELS[n.kind] || n.kind}</span>
                          {n.subject}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {n.channel === "push" ? "Push" : "E-mail"} · {n.audience === "admin" ? "Admin" : n.audience === "client" ? "Client" : n.company_name || "Entrepreneur"}{n.recipient ? ` · ${n.recipient}` : ""}
                        </p>
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">{fmtDate(n.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
